import { notifications } from '@mantine/notifications';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EventForm } from 'src/Components/Forms/Event/EventForm';
import { RoomConflict } from 'src/Components/Forms/Event/RoomSelector/RoomSelector.types';
import { useCreateEventForm } from 'src/Hooks/Forms/UseCreateEventForm';
import { transformRecurrenceDtoToFormValues } from 'src/Hooks/Forms/UseCreateEventForm/transformRecurrenceDtoToFormValues';
import { useUpdateEvent } from 'src/Hooks/Mutations/useUpdateEvent';
import useFetchEvent from 'src/Hooks/Queries/useFetchEvent';

import { Props } from './EventEditionFormWrapper.types';

const EVENT_ROOM_CONFLICT_CODE = 'EVENT_ROOM_CONFLICT';

interface RoomConflictErrorBody {
  code?: string;
  conflicts?: RoomConflict[];
}

const extractRoomConflicts = (error: unknown): RoomConflict[] | null => {
  const axiosError = error as AxiosError<RoomConflictErrorBody> | undefined;
  const body = axiosError?.response?.data;
  if (body?.code !== EVENT_ROOM_CONFLICT_CODE) return null;
  return body.conflicts ?? [];
};

const EventEditionFormWrapper = ({ onCreate, onCancel, eventId }: Props) => {
  const { data: event } = useFetchEvent(eventId);
  const form = useCreateEventForm();
  const { t } = useTranslation();
  const [roomConflicts, setRoomConflicts] = useState<RoomConflict[] | undefined>(undefined);
  const { mutate } = useUpdateEvent({
    onSuccess: () => {
      setRoomConflicts(undefined);
      onCreate();
    },
    onError: error => {
      const conflicts = extractRoomConflicts(error);
      if (conflicts) {
        setRoomConflicts(conflicts);
        return;
      }
      setRoomConflicts(undefined);
      notifications.show({
        color: 'red',
        message: t(
          'createEventForm.errors.submitFailed',
          'L’événement n’a pas pu être enregistré. Vérifiez votre connexion et réessayez.'
        ),
      });
    },
  });

  useEffect(() => {
    if (event) {
      form.setValues({
        name: event.name,
        type: event.type,
        description: event.description ?? '',
        placeId: event.address?.googlePlaceId ?? '',
        videoConferenceUrl: event.videoConferenceUrl ?? '',
        startTime: event.startTime.toDate(),
        endTime: event.endTime?.toDate(),
        isExclusiveOccupation: event.isExclusiveOccupation,
        recurrence: transformRecurrenceDtoToFormValues(event.recurrence),
        reminderDurationSeconds: event.reminderDurationSeconds?.toString(),
        participantIds: event.participants.map(p => p.id),
        householdIds: (event.households ?? []).map(household => household.id),
        roomIds: (event.rooms ?? []).map(room => room.id),
      });
    }
  }, [event]);

  return (
    <EventForm
      onCancel={onCancel}
      form={form}
      onSubmit={form.onSubmit(values => {
        setRoomConflicts(undefined);
        mutate({ eventId, data: values });
      })}
      submitButtonLabel={t('createEventForm.editEvent', 'Modifier')}
      initialAddressText={event?.address?.formattedAddress}
      roomConflicts={roomConflicts}
    />
  );
};

export default EventEditionFormWrapper;
